// src/components/tour_groups_calendar/bar/TourGroupsCalendarBar.tsx
import styled from "styled-components";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Drawer,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Tooltip,
  SelectChangeEvent,
  Autocomplete,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";

//----------------------------------------------------------------

const BarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  background-color: whitesmoke;
  flex: 1;
  z-index: 9999;
`;

const HeaderBarButtonContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  .header-button {
    color: #464646;
    font-size: 17px;
    cursor: pointer;
    &:hover {
      color: ${({ theme }) => theme.colors.blue};
    }
  }
`;

const RoutingContainer = styled.div`
  margin-left: 10px;
  @media (max-width: 700px) {
    display: none;
  }
`;

const DateSelectionContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  input {
    text-align: center;
    height: 30px;
    background-color: #fcfcfc;
    border-radius: 5px;
    border: 1px solid #eee;
    font-size: 12px;
    padding: 0 10px;
    width: 140px;
    font-family: "Roboto", sans-serif;
    cursor: pointer;
  }
`;

const FilterButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const closeIconAndDateVariants = {
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.0,
    },
  },
};

const DrawerContent = styled.div`
  width: 350px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    font-size: 18px;
  }
`;

//----------------------------------------------------------------

interface TourGroupsCalendarBarProps {
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void;
  entities: any;
}

interface ProductOption {
  _id: string;
  title: string;
  productTitle: string;
  productId: string;
}

const TourGroupsCalendarBar = ({
  searchParams,
  setSearchParams,
  entities,
}: TourGroupsCalendarBarProps) => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const startDate =
    searchParams.get("startDate") || moment().format("YYYY-MM-DD");
  const endDate = searchParams.get("endDate") || undefined;

  const selectedOptionIds = useMemo(() => {
    const optionIdsParam = searchParams.get("optionIds");
    return optionIdsParam ? optionIdsParam.split(",") : [];
  }, [searchParams]);

  const minSeatsCount = useMemo(() => {
    const minSeatsParam = searchParams.get("minSeats");
    return minSeatsParam ? parseInt(minSeatsParam, 10) : undefined;
  }, [searchParams]);

  const sortBy = (searchParams.get("sortBy") || "date") as "date" | "seats";

  const allProductOptions: ProductOption[] = (entities?.products || [])
    .flatMap((product: any) =>
      (product.options || []).map((option: any) => ({
        _id: option._id,
        title: option.title,
        productTitle: product.title || `BOKUN #${product.bokun_product_code}`,
        productId: product._id,
      }))
    )
    .sort((a: ProductOption, b: ProductOption) => {
      const productCompare = a.productTitle.localeCompare(b.productTitle);
      if (productCompare !== 0) return productCompare;
      return a.title.localeCompare(b.title);
    });

  const selectedProductOptions = allProductOptions.filter((option) =>
    selectedOptionIds.includes(option._id)
  );

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      const newStartDate = moment(date).format("YYYY-MM-DD");
      const newParams = new URLSearchParams(searchParams);
      if (endDate) {
        const isValid = moment(newStartDate).isSameOrBefore(endDate);
        if (isValid) {
          newParams.set("startDate", newStartDate);
          setSearchParams(newParams);
        } else {
          alert("Start date must be before or equal to end date");
        }
      } else {
        newParams.set("startDate", newStartDate);
        setSearchParams(newParams);
      }
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (date) {
      const newEndDate = moment(date).format("YYYY-MM-DD");
      const isValid = moment(newEndDate).isSameOrAfter(startDate);
      if (isValid) {
        newParams.set("endDate", newEndDate);
        setSearchParams(newParams);
      } else {
        alert("End date must be after or equal to start date");
      }
    } else {
      newParams.delete("endDate");
      setSearchParams(newParams);
    }
  };

  const handleProductOptionChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newSelectedOptions: ProductOption[]
  ) => {
    const newParams = new URLSearchParams(searchParams);
    if (newSelectedOptions.length > 0) {
      const optionIds = newSelectedOptions.map((option) => option._id).join(",");
      newParams.set("optionIds", optionIds);
    } else {
      newParams.delete("optionIds");
    }
    setSearchParams(newParams);
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sortBy", event.target.value);
    setSearchParams(newParams);
  };

  const handleMinSeatsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newParams = new URLSearchParams(searchParams);
    const value = event.target.value;
    if (value) {
      newParams.set("minSeats", value);
    } else {
      newParams.delete("minSeats");
    }
    setSearchParams(newParams);
  };

  const activeFiltersCount =
    (selectedOptionIds.length > 0 ? 1 : 0) +
    (minSeatsCount ? 1 : 0) +
    (sortBy !== "date" ? 1 : 0);

  return (
    <>
      <BarContainer>
        <AnimatePresence>
          <HeaderBarButtonContainer
            variants={closeIconAndDateVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={() => navigate("/home")}
          >
            <i className="fa-solid fa-home header-button"></i>
            <RoutingContainer>
              <i
                style={{
                  fontSize: "8px",
                }}
                className="fa-solid fa-angle-right"
              ></i>
              <span
                style={{
                  fontSize: "10px",
                  marginLeft: "5px",
                }}
              >
                Tour Groups Calendar
              </span>
            </RoutingContainer>
          </HeaderBarButtonContainer>
        </AnimatePresence>
        <AnimatePresence>
          <DateSelectionContainer
            variants={closeIconAndDateVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <DatePicker
              selected={moment(startDate).toDate()}
              onChange={handleStartDateChange}
              dateFormat="dd MMM yyyy"
            />
            <span>to</span>
            <DatePicker
              selected={endDate ? moment(endDate).toDate() : null}
              onChange={handleEndDateChange}
              isClearable
              placeholderText="Optional"
              dateFormat="dd MMM yyyy"
            />
          </DateSelectionContainer>
        </AnimatePresence>
        <FilterButtonContainer>
          <Tooltip title="Filters & Sort">
            <IconButton
              onClick={() => setDrawerOpen(true)}
              color={activeFiltersCount > 0 ? "primary" : "default"}
            >
              <FilterAltIcon />
              {activeFiltersCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    backgroundColor: "#1976d2",
                    color: "white",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    fontSize: 11,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </IconButton>
          </Tooltip>
        </FilterButtonContainer>
      </BarContainer>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <DrawerContent>
          <DrawerHeader>
            <h3>Filters & Sort</h3>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DrawerHeader>

          <FormControl fullWidth>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} onChange={handleSortChange} label="Sort By">
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="seats">Number of Seats</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Minimum Seats"
            type="number"
            value={minSeatsCount || ""}
            onChange={handleMinSeatsChange}
            fullWidth
            inputProps={{ min: 0 }}
          />

          <Autocomplete
            multiple
            options={allProductOptions}
            value={selectedProductOptions}
            onChange={handleProductOptionChange}
            getOptionLabel={(option) => `${option.productTitle} - ${option.title}`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Product Options"
                placeholder="Select product options..."
              />
            )}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
            }}
          />

          <Button
            variant="outlined"
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("optionIds");
              newParams.delete("minSeats");
              newParams.set("sortBy", "date");
              setSearchParams(newParams);
            }}
          >
            Clear All Filters
          </Button>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default TourGroupsCalendarBar;
